import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { clearFeuilletage } from "../store/actions/feuilletageActions"

/**
 * Feuilletage clavier Page Up / Page Down entre fiches d'une même liste —
 * dépannage, atelier et location (généralisation d'`useAtelierFeuilletage`,
 * qui était le seul mécanisme sain des trois).
 *
 * Deux propriétés qui font tout marcher :
 *  - la liste est FIGÉE par la page-liste au moment d'ouvrir une fiche (ordre
 *    du tableau, tri et filtres compris) ;
 *  - l'index courant est DÉRIVÉ du param d'URL, jamais compté à la main : il
 *    ne peut pas se désynchroniser, même si une navigation ne passe pas par
 *    les touches (ancien défaut du dépannage) et même après un reset redux.
 *
 * Le garde-fou « modifications non sauvegardées » est gratuit : on navigue
 * par `history.push`, donc le `<Prompt>` de `useQuitGuard` intercepte.
 *
 * Purge : si la fiche affichée n'appartient pas à la liste, c'est qu'on est
 * entré par un point SANS liste (fiche client, dispatch, lien direct,
 * création, fiche liée) → la liste est vidée et le feuilletage devient
 * inerte. Corollaire voulu : revenir sur une fiche de la liste (bouton nav
 * « Fiche ») retrouve son feuilletage sans que personne n'ait à le rétablir.
 *
 * Suspension : tant qu'un modal est ouvert, le feuilletage est inerte. Sans
 * ça, un PageDown sous un modal de sauvegarde ferait défiler la fiche
 * DERRIÈRE le modal, qui posterait alors la fiche suivante en croyant
 * enregistrer la précédente. L'ancien handler dépannage avait ce garde-fou,
 * l'atelier ne l'a jamais eu — il est désormais commun aux trois.
 *
 * @param domaine  "dep" | "ate" | "loc"
 * @param route    préfixe d'URL de la fiche, ex. "/atelier/fiche"
 * @param suspendu vrai quand un modal est ouvert (la fiche passe son propre
 *                 « un modal est à l'écran », verrou compris)
 */
export default function useFeuilletage(domaine, route, suspendu = false) {
	const history = useHistory()
	const { id } = useParams()
	const dispatch = useDispatch()
	const ids = useSelector((s) => s.feuilletageInfo[domaine].ids)

	const appartientALaListe = ids.some((x) => String(x) === String(id))

	useEffect(() => {
		if (ids.length > 0 && !appartientALaListe)
			dispatch(clearFeuilletage(domaine))
	}, [ids.length, appartientALaListe, domaine, dispatch])

	// Listener monté une seule fois → valeurs courantes lues via une ref.
	const live = useRef({})
	live.current = { id, ids, history, route, suspendu }

	useEffect(() => {
		const onKeyDown = (e) => {
			if (e.key !== "PageUp" && e.key !== "PageDown") return
			const { id, ids, history, route, suspendu } = live.current
			if (suspendu) return
			// Tiroir / modal d'assistance ouvert (GE-59 C6).
			if (document.querySelector("[data-assistance-ui]")) return
			if (!ids || ids.length === 0) return
			const index = ids.findIndex((x) => String(x) === String(id))
			if (index === -1) return
			let cible = -1
			if (e.key === "PageUp" && index > 0) cible = index - 1
			if (e.key === "PageDown" && index < ids.length - 1)
				cible = index + 1
			if (cible === -1) return
			e.preventDefault()
			history.push(`${route}/${ids[cible]}`)
		}
		document.addEventListener("keydown", onKeyDown)
		return () => document.removeEventListener("keydown", onKeyDown)
	}, [])
}
